/* Ponte fra `strategy-icon.js` (script globale, copiato dal prototipo senza
 * toccarne una riga) e il resto dell'applicazione, che è a moduli ES.
 *
 * Il file originale è un IIFE che si registra su `window.SemBotIcon` e accetta
 * `React` come primo argomento di `tile`. Non lo si riscrive perché la sua
 * garanzia — 65 firme distinte su 65 nomi, colori a massima distanza
 * percettiva, nessun verde/rosso/teal — è il risultato di una taratura, non di
 * una formula che si possa ricostruire a occhio.
 */
import type { ReactElement } from 'react';
import * as React from 'react';
import './strategy-icon.js';
import { NOMI_STRATEGIE } from './nomi-strategie';

export interface InfoIcona {
  family: string;
  variant: string;
  color: { a: string; b: string };
  cells: boolean[];
}

export interface VerificaIcone {
  total: number;
  uniqueSignatures: number;
  families: number;
  ok: boolean;
  minColorDistance: number;
}

export interface FamigliaStrategie {
  family: string;
  color: string;
  members: string[];
}

interface ApiIcona {
  setNames(list: readonly string[]): ApiIcona;
  tile(react: typeof React, name: string, size: number): ReactElement;
  info(name: string): InfoIcona;
  parse(name: string): { family: string; variant: string };
  check(): VerificaIcone;
  palette(): string[];
  families(): FamigliaStrategie[];
}

declare global {
  interface Window {
    SemBotIcon?: ApiIcona;
  }
}

let registrato = false;

function api(): ApiIcona | null {
  const I = typeof window !== 'undefined' ? window.SemBotIcon : undefined;
  if (!I) return null;
  /* L'elenco si registra una volta sola. Ri-registrarlo a ogni chiamata
     azzererebbe la cache interna del generatore a ogni render. */
  if (!registrato) {
    I.setNames(NOMI_STRATEGIE);
    registrato = true;
  }
  return I;
}

/** Elemento React dell'icona, o `null` se il generatore non è disponibile. */
export function tesseraStrategia(nome: string, dimensione = 26): ReactElement | null {
  const I = api();
  if (!I) return null;
  try {
    return I.tile(React, nome, dimensione);
  } catch {
    return null;
  }
}

/** Famiglia, variante e colore dedotti dal nome. Utile per raggruppare. */
export function infoStrategia(nome: string): InfoIcona | null {
  const I = api();
  if (!I) return null;
  try {
    return I.info(nome);
  } catch {
    return null;
  }
}

/** Le famiglie dedotte dall'elenco corrente, per ispezione. */
export function famiglieStrategie(): FamigliaStrategie[] {
  return api()?.families() ?? [];
}

/** Controllo di unicità delle firme: `ok` falso significa due icone identiche. */
export function verificaIcone(): VerificaIcone | null {
  return api()?.check() ?? null;
}
